import { Box, Flex, IconButton, Text } from "@radix-ui/themes";
import { useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsDesktop } from "@/hooks/use-media-query";
import { AppVersion } from "@/components/app-version";
import { LanguageSwitcher } from "@/components/ui/language-switcher";

function resolveTitle(pathname: string, t: (key: string) => string): string {
  if (pathname === "/dashboard") return t("nav.dashboard");
  if (pathname.startsWith("/boards/")) return t("nav.board");
  return t("common.app_name");
}

interface TopbarProps {
  showMenuButton?: boolean;
  onMenuClick?: () => void;
}

export function Topbar({ showMenuButton = false, onMenuClick }: TopbarProps) {
  const { location } = useRouterState();
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();
  const title = resolveTitle(location.pathname, t);

  return (
    <Box
      px={{ initial: "3", sm: "5" }}
      py="3"
      style={{
        borderBottom: "1px solid var(--gray-4)",
        backgroundColor: "var(--color-background)",
        paddingTop: "max(env(safe-area-inset-top), var(--space-3))",
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <Flex align="center" justify="between" gap="2" style={{ minWidth: 0 }}>
        <Flex align="center" gap="2" style={{ minWidth: 0, flex: 1 }}>
          {showMenuButton ? (
            <IconButton
              variant="ghost"
              size="2"
              aria-label={t("auth.open_menu")}
              onClick={onMenuClick}
            >
              <Menu size={18} />
            </IconButton>
          ) : null}
          <Text size="3" weight="medium" truncate>
            {title}
          </Text>
        </Flex>
        <Flex align="center" gap="1" style={{ flexShrink: 0 }}>
          <LanguageSwitcher compact={!isDesktop} />
          <AppVersion compact={!isDesktop} />
        </Flex>
      </Flex>
    </Box>
  );
}
